import * as React from 'react';
import { useState } from 'react';
import AppShell from '../chrome/AppShell';
import {
    Chipbar,
    MenuItem,
    MenuLabel,
    MenuSep,
    TCard,
    TEmpty,
    TFoot,
    Toolbar,
    TRow,
} from '../components/table';
import {
    IconActivations,
    IconDevices,
    IconGallery,
    IconHome,
    IconSearch,
    IconSharing,
} from '../chrome/icons';
import {
    Checkbox,
    ConfirmDialog,
    Dialog,
    Field,
    SaveBar,
    SectionCard,
    Select,
    Textarea,
    TextInput,
    Toggle,
    ToggleRow,
} from '../components/forms';

/*
 * The v3 kit — the vocabulary, rendered by the REAL components and
 * classes (v3.css + components/table.tsx), so it can't drift from the
 * product. The v2 equivalent (/v2/kit) proved its worth; this is its
 * successor. Not in the rail — a workbench, reached at /kit (and from
 * the dev toolbar).
 */

function Swatch({ name, varName }: { name: string; varName: string }) {
    return (
        <div style={{ width: 108 }}>
            <div
                style={{
                    height: 40,
                    borderRadius: 8,
                    background: `var(${varName})`,
                    border: '1px solid var(--border-tertiary)',
                }}
            />
            <div style={{ fontSize: 12, marginTop: 4 }}>{name}</div>
            <div
                className="s-lab"
                style={{ margin: 0, textTransform: 'none', letterSpacing: 0 }}
            >
                {varName}
            </div>
        </div>
    );
}

function KitSection({
    title,
    children,
}: {
    title: string;
    children: React.ReactNode;
}) {
    return (
        <div className="sec">
            <div className="sec-h">
                <h2>{title}</h2>
            </div>
            {children}
        </div>
    );
}

const Row = ({ children }: { children: React.ReactNode }) => (
    <div
        style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            flexWrap: 'wrap',
            padding: '6px 0',
        }}
    >
        {children}
    </div>
);

export default function KitV3() {
    const [query, setQuery] = useState('');
    const [seg, setSeg] = useState<'a' | 'b'>('b');
    const [chipOn, setChipOn] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [toggleOn, setToggleOn] = useState(true);
    const setToggleOn2 = (on: boolean) => setToggleOn(on);
    const [checkOn, setCheckOn] = useState(true);

    return (
        <AppShell>
            <h1 className="pg-title">Kit</h1>
            <p className="pg-sub">
                The v3 vocabulary, rendered by the real components — tokens
                from the Cursor Agents kit (docs/design-brief/), decisions
                from the archetype mocks.
            </p>

            <div className="dets" style={{ maxWidth: 760 }}>
                <div className="stack">
                    <KitSection title="Surfaces & primitives">
                        <Row>
                            <Swatch name="Base (ink)" varName="--base" />
                            <Swatch name="Chrome" varName="--chrome" />
                            <Swatch name="Sidebar" varName="--sidebar" />
                            <Swatch name="Elevated" varName="--elevated" />
                            <Swatch name="Live" varName="--live" />
                            <Swatch name="Good" varName="--good" />
                            <Swatch name="Focus" varName="--focus" />
                        </Row>
                        <p
                            className="quiet-empty"
                            style={{ marginTop: 8, fontSize: 12 }}
                        >
                            Every neutral is --base at an alpha (text
                            74/60/36 · icons 66/52 · fills 8/6 · borders
                            12/8/4). Surfaces above the chrome are elevated
                            + border-tertiary + soft shadow — never a bare
                            border-only card.
                        </p>
                    </KitSection>

                    <KitSection title="Type">
                        <p className="pg-title" style={{ margin: 0 }}>
                            Page title · 20/28 · 600
                        </p>
                        <p
                            style={{
                                fontSize: 14,
                                lineHeight: '20px',
                                fontWeight: 600,
                                margin: '6px 0 0',
                            }}
                        >
                            Section heading · 14/20 · 600
                        </p>
                        <p style={{ margin: '6px 0 0' }}>
                            Body · 13/18 · 400 — hierarchy comes from size
                            and alpha, not weight.
                        </p>
                        <p
                            style={{
                                fontSize: 12,
                                lineHeight: '16px',
                                color: 'var(--text-tertiary)',
                                margin: '6px 0 0',
                            }}
                        >
                            Meta · 12/16 · text-tertiary
                        </p>
                        <p className="s-lab" style={{ margin: '10px 0 0' }}>
                            Mono micro-label · 9px · 600 · tracked
                        </p>
                    </KitSection>

                    <KitSection title="Controls">
                        <Row>
                            <button className="tb-new">Primary</button>
                            <button className="tb-btn">Outline</button>
                            <button className="tb-btn tb-icon" aria-label="Icon">
                                <IconSearch />
                            </button>
                            <span className="seg">
                                <button
                                    className={seg === 'a' ? 'on' : ''}
                                    onClick={() => setSeg('a')}
                                >
                                    Test
                                </button>
                                <button
                                    className={seg === 'b' ? 'on' : ''}
                                    onClick={() => setSeg('b')}
                                >
                                    Live
                                </button>
                            </span>
                            <span className="badge">Badge</span>
                        </Row>
                        <Row>
                            <label className="tb-search" style={{ maxWidth: 280 }}>
                                <IconSearch />
                                <input
                                    value={query}
                                    placeholder="Search Kit"
                                    onChange={(e) => setQuery(e.target.value)}
                                />
                            </label>
                        </Row>
                        <Chipbar
                            chips={
                                chipOn
                                    ? [
                                          {
                                              label: 'Location',
                                              value: 'Austin',
                                              onClear: () => setChipOn(false),
                                          },
                                      ]
                                    : []
                            }
                        />
                        {!chipOn && (
                            <button
                                className="tb-btn"
                                onClick={() => setChipOn(true)}
                            >
                                Restore chip
                            </button>
                        )}
                    </KitSection>

                    <KitSection title="Menu">
                        <div
                            className="menu"
                            style={{
                                position: 'static',
                                display: 'inline-block',
                                minWidth: 190,
                            }}
                        >
                            <MenuLabel>Date</MenuLabel>
                            <MenuItem on onClick={() => {}}>
                                This week
                            </MenuItem>
                            <MenuItem onClick={() => {}}>
                                Next 30 days
                            </MenuItem>
                            <MenuSep />
                            <MenuLabel>Sort by</MenuLabel>
                            <MenuItem on onClick={() => {}}>
                                Date
                            </MenuItem>
                            <MenuItem onClick={() => {}}>Name</MenuItem>
                        </div>
                    </KitSection>

                    <KitSection title="Statuses">
                        <Row>
                            <span style={{ color: 'var(--live)', fontSize: 12 }}>
                                ● Live
                            </span>
                            <span style={{ color: 'var(--good)', fontSize: 12 }}>
                                Ready
                            </span>
                            <span
                                style={{
                                    color: 'var(--text-tertiary)',
                                    fontSize: 12,
                                }}
                            >
                                Wrapped · No devices assigned (absence is
                                quiet, never a warning)
                            </span>
                            <span className="pulse" title="online pulse" />
                            <span className="pulse off" title="offline" />
                        </Row>
                    </KitSection>

                    <KitSection title="Rail rows (chrome)">
                        <div style={{ maxWidth: 244, background: 'var(--sidebar)', borderRadius: 8, padding: 8 }}>
                            <div className="nav">
                                <button className="row">
                                    <IconHome />
                                    Home
                                </button>
                                <button className="row active">
                                    <IconActivations />
                                    Activations
                                </button>
                                <button className="row">
                                    <IconDevices />
                                    Devices
                                </button>
                            </div>
                            <div className="ql-lab" style={{ marginTop: 10 }}>
                                Quick links
                            </div>
                            <div className="ql">
                                <IconGallery />
                                Public gallery
                            </div>
                            <div className="ql">
                                <IconSharing />
                                Sharing station
                            </div>
                        </div>
                    </KitSection>

                    <KitSection title="Form fields (exemplar-5 vocabulary, kit §8.15)">
                        <Field
                            label="Gallery name"
                            help="Shown on the public gallery."
                        >
                            <TextInput placeholder="ACL Weekend One" />
                        </Field>
                        <Field label="Slug" error="This address is taken.">
                            <TextInput defaultValue="acl-weekend-one" aria-invalid="true" />
                        </Field>
                        <Field label="Capture app">
                            <Select defaultValue="clickerbooth">
                                <option value="clickerbooth">
                                    clickerbooth
                                </option>
                                <option value="romr">ROMR</option>
                            </Select>
                        </Field>
                        <Field label="Notes">
                            <Textarea placeholder="Anything the crew should know…" />
                        </Field>
                        <Field label="Disabled">
                            <TextInput disabled value="Read-only value" />
                        </Field>
                    </KitSection>

                    <KitSection title="Choices">
                        <Row>
                            <Checkbox
                                label="Hold new photos for approval"
                                checked={checkOn}
                                onChange={(e) => setCheckOn(e.target.checked)}
                            />
                        </Row>
                        <Row>
                            <Toggle
                                label="Save-For-Later button"
                                checked={toggleOn}
                                onChange={(e) =>
                                    setToggleOn(e.target.checked)
                                }
                            />
                        </Row>
                    </KitSection>

                    <KitSection title="Dialogs">
                        <Row>
                            <button
                                className="tb-btn"
                                onClick={() => setDialogOpen(true)}
                            >
                                Open dialog
                            </button>
                            <button
                                className="tb-btn danger-soft"
                                onClick={() => setConfirmOpen(true)}
                            >
                                Destructive confirm…
                            </button>
                            <button className="tb-new danger">
                                Danger primary
                            </button>
                        </Row>
                        <p
                            className="quiet-empty"
                            style={{ marginTop: 8, fontSize: 12 }}
                        >
                            Archive is soft and confirms nothing; delete and
                            revoke get the danger confirm. One dialog width
                            (440), title · body · right-aligned actions.
                        </p>
                    </KitSection>

                    <SectionCard
                        title="Settings-form pieces"
                        note="rule 51: one semantic per section"
                    >
                        <ToggleRow
                            label="Email"
                            sub="an immediate section — flip = saved"
                            checked={toggleOn}
                            onChange={setToggleOn2}
                        />
                        <div style={{ marginTop: 10 }}>
                            <SaveBar
                                dirty={['Email', 'SMS']}
                                onSave={() => {}}
                                onDiscard={() => {}}
                            />
                        </div>
                    </SectionCard>

                    <KitSection title="Interaction policy (interfaces.rauno.me)">
                        <ul
                            style={{
                                margin: 0,
                                paddingLeft: 18,
                                color: 'var(--text-tertiary)',
                                fontSize: 12,
                                lineHeight: '18px',
                            }}
                        >
                            <li>Hover styles only under @media (hover: hover); taps get :active feedback instead.</li>
                            <li>Inputs are 16px below 768px (no iOS zoom), default spellcheck/autocomplete off, labels focus them.</li>
                            <li>Menus open on mousedown; Enter and Space work everywhere a pointer does.</li>
                            <li>Numbers are tabular wherever they form columns; weights never change on hover.</li>
                            <li>Dialogs: 150ms fade + scale from 0.96, reduced-motion aware; Enter submits (real form).</li>
                            <li>Copy feedback is an inline check on the trigger — never a toast. Errors tint their own input.</li>
                            <li>No dead controls: an affordance ships with its behavior or not at all.</li>
                        </ul>
                    </KitSection>

                    <KitSection title="Feedback & media">
                        <div className="banner" style={{ marginBottom: 12 }}>
                            <span className="tag">Test plane</span>
                            Captures and shares made now never mix with live
                            data.
                            <button className="act">Clear test data…</button>
                        </div>
                        <div className="toast" style={{ marginBottom: 12 }}>
                            <span className="tile">
                                <IconSharing />
                            </span>
                            <span style={{ minWidth: 0 }}>
                                <span className="t1">Call sheet sent</span>
                                <br />
                                <span className="t2">
                                    Maria G · also in the portal
                                </span>
                            </span>
                        </div>
                        <div style={{ marginBottom: 12 }}>
                            <div className="prop">
                                <span className="fl">
                                    <span className="flag" />
                                    Load-in
                                </span>
                                <span className="vals">
                                    <span className="cur">3:00 PM</span>
                                    <span className="arr">→</span>
                                    <span>1:30 PM</span>
                                </span>
                                <span className="ops">
                                    <button className="tb-new sm">
                                        Approve
                                    </button>
                                    <button className="tb-btn sm">
                                        Decline…
                                    </button>
                                </span>
                            </div>
                        </div>
                        <div
                            className="progress"
                            style={{ maxWidth: 280, marginBottom: 12 }}
                        >
                            <div className="fill" style={{ width: '62%' }} />
                        </div>
                        <div
                            className="dropzone"
                            style={{ maxWidth: 340, marginBottom: 12 }}
                        >
                            Drop files, or click to browse
                        </div>
                        <Row>
                            <span className="thumb" />
                            <span className="thumb" />
                            <span className="sbadge">3 waiting</span>
                            <span className="kbd">⌘K</span>
                            <a className="link" href="/kit">
                                Text link
                            </a>
                        </Row>
                    </KitSection>
                </div>

                <Dialog
                    open={dialogOpen}
                    title="New activation"
                    onClose={() => setDialogOpen(false)}
                    footer={
                        <>
                            <button
                                className="tb-btn"
                                onClick={() => setDialogOpen(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="tb-new"
                                onClick={() => setDialogOpen(false)}
                            >
                                Create
                            </button>
                        </>
                    }
                >
                    <Field label="Name">
                        <TextInput placeholder="Event name" />
                    </Field>
                    <Field label="Date">
                        <TextInput type="date" />
                    </Field>
                </Dialog>
                <ConfirmDialog
                    open={confirmOpen}
                    title="Revoke moderation link?"
                    body="Anyone holding the link loses access immediately. A new link can be minted at any time."
                    actionLabel="Revoke link"
                    onConfirm={() => {}}
                    onClose={() => setConfirmOpen(false)}
                />

                <div style={{ height: 16 }} />

                <div className="vitals">
                    <div style={{ flex: 1 }}>
                        <p className="s-lab">Stat tiles</p>
                        <div className="tiles">
                            <div className="tile">
                                <p className="tl">Captures</p>
                                <div className="tn">1,284</div>
                            </div>
                            <div className="tile">
                                <p className="tl">Shares</p>
                                <div className="tn">972</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ height: 16 }} />

                <p className="s-lab">Table archetype (real components)</p>
                <Toolbar
                    searchValue={query}
                    onSearch={setQuery}
                    searchPlaceholder="Search Activations"
                    filterCount={chipOn ? 1 : 0}
                    primaryLabel="New activation"
                    onPrimary={() => {}}
                    menu={(close) => (
                        <>
                            <MenuLabel>Date</MenuLabel>
                            <MenuItem on onClick={close}>
                                This week
                            </MenuItem>
                            <MenuItem onClick={close}>All time</MenuItem>
                        </>
                    )}
                />
                <TCard>
                    <TRow
                        name="Chi Omega Bid Day"
                        meta="The Castilian, Austin"
                        metaDate="Today"
                        date="Today"
                        status={
                            <>
                                <span className="live">● Live</span> · 2
                                devices
                            </>
                        }
                        onOpen={() => {}}
                        menuItems={[
                            { label: 'Open', onClick: () => {} },
                            { label: 'Clone…', onClick: () => {} },
                            { label: 'Archive', onClick: () => {} },
                        ]}
                    />
                    <TRow
                        name="Toyota Hall Tour"
                        badge="Archived"
                        meta="Fair Park, Dallas"
                        metaDate="Feb 2 ’25"
                        date="Feb 2 ’25"
                        status="Wrapped"
                        onOpen={() => {}}
                        menuItems={[
                            { label: 'Open', onClick: () => {} },
                            { label: 'Unarchive', onClick: () => {} },
                        ]}
                    />
                    <TEmpty
                        message="Empty states are honest — one sentence, one widening move."
                        actionLabel="Show next 30 days"
                        onAction={() => {}}
                    />
                </TCard>
                <TFoot>2 of 143 · This week — the footer states scope honestly</TFoot>
            </div>
        </AppShell>
    );
}
